// The Workflow Builder, a full-screen workspace:
//   top bar · AI panel | trail or steps list, plus a full-screen viewer
//   for the clicked source, step or output
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, ConfigProvider, Spin, Upload } from "antd"; // Segmented: trail/list toggle (switched off)
import { FileOutput, FilePlus2, Play } from "lucide-react"; // Roadmap 1 also used Plus
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { describeNewSource, firstSheet, readWorkbook } from "../shared/sourceFiles";
// import AddStepDialog from "./AddStepDialog"; // Roadmap 1
import AiPanel from "./AiPanel";
import { BuilderContext } from "./BuilderContext";
import TrailView from "./trail/TrailView";
// import ListView from "./ListView"; // Steps list switched off for now
import NodeViewer from "./NodeViewer";
import RunDialog from "./RunDialog";
import StartScreen from "./StartScreen";
import TopBar from "./TopBar";
import UseWorkflowScreen from "./UseWorkflowScreen";
import { useWorkflowEditor } from "./useWorkflowEditor";
import VersionsDialog from "./VersionsDialog";

export const BUILDER_THEME = {
  token: {
    colorPrimary: "#0f766e",
    colorLink: "#0f766e",
    borderRadius: 6,
    fontSize: 13,
    fontFamily: '"IBM Plex Sans", system-ui, sans-serif',
    fontFamilyCode: '"IBM Plex Mono", ui-monospace, monospace',
  },
};

const isTyping = (target) => ["INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName) || target?.isContentEditable;

const FilePicker = ({ onFiles, children }) => (
  <Upload
    multiple
    accept=".csv,.xlsx,.txt"
    showUploadList={false}
    beforeUpload={(file, list) => {
      // Called once per file; hand over the whole selection on the first call.
      if (file === list[0]) onFiles(list);
      return false;
    }}
  >
    {children}
  </Upload>
);

const WorkflowBuilder = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const { state, dispatch, run, running, rerun, outputs, loading, saving, save, reload, aiConversation } = useWorkflowEditor({
    workflowId: isNew ? null : id,
    projectId: searchParams.get("project"),
  });
  // Steps list switched off for now; the trail is the only view.
  // const [view, setView] = useState("trail");
  const view = "trail";
  const [fitVersion, setFitVersion] = useState(0);
  // Roadmap 1: { afterId?, inputId? } while the step palette is open (palette switched off).
  const [, setAddStepAt] = useState(null);
  const [dialog, setDialog] = useState(null); // "run" | "versions"
  const [talkingAbout, setTalkingAbout] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  // A saved workflow opened without files starts on the "Use this workflow"
  // screen (upload → check → run), not on an empty trail.
  const [usingSaved, setUsingSaved] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;

  // Close the viewer when its node is deleted.
  useEffect(() => {
    if (!state.selectedId) setViewerOpen(false);
  }, [state.selectedId]);

  const workflow = state.workflow;
  const canEdit = !workflow?._id || Boolean(workflow?.access?.edit);

  // Decided once per opened workflow: after saving a new one, its files are
  // already loaded, so the screen doesn't show.
  useEffect(() => {
    const current = stateRef.current;
    if (current.workflow?._id) setUsingSaved(current.workflow.sources.some((s) => !current.files[s.id]));
  }, [workflow?._id]);

  // Each file fills the first source that has no file yet (e.g. a saved
  // workflow being reused); only when every source has one is a new source added.
  const addFiles = useCallback(
    async (fileList) => {
      const filled = new Set(); // sources filled by this selection (state updates later)
      for (const file of fileList) {
        try {
          const workbook = await readWorkbook(file);
          const { workflow: current, files } = stateRef.current;
          const waiting = current.sources.find((s) => !files[s.id] && !filled.has(s.id));
          if (waiting) {
            filled.add(waiting.id);
            dispatch({ type: "attachSample", id: waiting.id, file: workbook });
            continue;
          }
          const source = await describeNewSource(workbook, firstSheet(workbook).name);
          dispatch({ type: "addSource", file: workbook, source });
        } catch (error) {
          toast.error(`Could not read ${file.name}: ${error.message}`);
        }
      }
    },
    [dispatch],
  );

  const addStep = useCallback(
    (stepType, afterId, inputId) => {
      const { workflow: current, selectedId } = stateRef.current;
      const selectedStep = current.steps.some((s) => s.id === selectedId) ? selectedId : undefined;
      dispatch({
        type: "addStep",
        stepType,
        afterId: afterId ?? selectedStep ?? current.steps[current.steps.length - 1]?.id,
        inputId,
      });
    },
    [dispatch],
  );

  const handleSave = useCallback(async () => {
    const saved = await save();
    if (saved && isNew) navigate(`/portal/workflows/${saved._id}`, { replace: true });
  }, [save, isNew, navigate]);

  // Keyboard shortcuts: S or ⌘K add a step, ⌘S saves, ⌘Z / ⇧⌘Z undo/redo.
  useEffect(() => {
    const onKey = (event) => {
      // The node viewer is where steps are edited, so shortcuts stay on there.
      if (isTyping(event.target) || document.querySelector(".ant-modal-wrap:not(.node-viewer)")) return;
      const mod = event.metaKey || event.ctrlKey;
      const key = event.key.toLowerCase();
      // Roadmap 1: S or ⌘K opened the built-in step palette.
      // if ((mod && key === "k") || (!mod && !event.altKey && key === "s" && canEdit)) {
      //   event.preventDefault();
      //   setAddStepAt({});
      // } else
      if (mod && key === "s") {
        event.preventDefault();
        if (canEdit) handleSave();
      } else if (mod && key === "z") {
        event.preventDefault();
        dispatch({ type: event.shiftKey ? "redo" : "undo" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dispatch, handleSave, canEdit]);

  // Warn before leaving with unsaved changes.
  useEffect(() => {
    if (!state.dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [state.dirty]);

  const context = useMemo(
    () => ({
      state,
      run,
      outputs,
      dispatch,
      select: (selectedId) => dispatch({ type: "select", id: selectedId }),
      // Selects a node and shows it in the full-screen viewer.
      openNode: (selectedId) => {
        dispatch({ type: "select", id: selectedId });
        setViewerOpen(true);
      },
      addStep,
      openAddStep: (at = {}) => setAddStepAt(at),
      talkingAbout,
      setTalkingAbout,
      aiConversation,
    }),
    [state, run, outputs, dispatch, addStep, talkingAbout, aiConversation],
  );

  if (loading || !workflow) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spin />
      </div>
    );
  }
  if (isNew && !workflow.projectID) {
    return <div className="p-6 text-sm text-gray-500">Choose a project first: open Workflows and click “New workflow”.</div>;
  }

  const empty = !workflow.sources.length && !workflow.steps.length;

  return (
    <ConfigProvider theme={BUILDER_THEME}>
      <BuilderContext.Provider value={context}>
        <div className="flex h-full flex-col bg-white text-gray-900">
          <TopBar
            onSave={handleSave}
            saving={saving}
            canEdit={canEdit}
            onRun={() => setDialog("run")}
            onVersions={() => setDialog("versions")}
          />
          <div className="flex min-h-0 flex-1">
            {/* The start screen already offers upload, AI and templates. */}
            {!empty && <AiPanel onRun={() => setDialog("run")} />}

            <main className="flex min-w-0 flex-1 flex-col">
              {!empty && (
                <div className="flex h-12 shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4">
                  {/* Steps list switched off for now:
                  <Segmented value={view} onChange={setView} options={[{ value: "trail", label: "Trail" }, { value: "list", label: "Steps list" }]} />
                  <span className="hidden text-xs text-gray-500 lg:inline">Same flow, two views — reorder in the list, branch on the trail</span> */}
                  <div className="ml-auto flex items-center gap-1.5">
                    {/* Re-runs the saved code on the loaded files (no AI involved). */}
                    <Button
                      size="small"
                      type="primary"
                      icon={<Play size={13} />}
                      loading={running}
                      disabled={!workflow.steps.length || workflow.sources.some((s) => !state.files[s.id])}
                      onClick={rerun}
                    >
                      Run
                    </Button>
                  {canEdit && (
                    <>
                      <FilePicker onFiles={addFiles}>
                        <Button size="small" icon={<FilePlus2 size={13} />}>
                          Source
                        </Button>
                      </FilePicker>
                      <Button size="small" icon={<FileOutput size={13} />} onClick={() => dispatch({ type: "addOutput" })}>
                        Output
                      </Button>
                      {/* Roadmap 1 (built-in step palette):
                      <Button size="small" icon={<Plus size={13} />} onClick={() => setAddStepAt({})}>
                        Add step <kbd className="ml-1 rounded border border-gray-300 px-1 font-mono text-[10px] text-gray-500">S</kbd>
                      </Button> */}
                      {view === "trail" && (
                        <Button size="small" onClick={() => setFitVersion((v) => v + 1)}>
                          Fit to screen
                        </Button>
                      )}
                    </>
                  )}
                  </div>
                </div>
              )}

              <div className="relative min-h-0 flex-1 bg-slate-50">
                {empty ? (
                  <StartScreen onAddFiles={addFiles} />
                ) : usingSaved ? (
                  <UseWorkflowScreen onContinue={() => setUsingSaved(false)} />
                ) : (
                  <TrailView fitVersion={fitVersion} />
                )}
                {/* Steps list switched off for now: view === "list" rendered <ListView /> here. */}
              </div>

            </main>
          </div>
        </div>

        <NodeViewer open={viewerOpen && !empty} onClose={() => setViewerOpen(false)} />
        {/* Roadmap 1 (built-in step palette):
        <AddStepDialog
          open={Boolean(addStepAt)}
          onClose={() => setAddStepAt(null)}
          onPick={(type) => {
            addStep(type, addStepAt?.afterId, addStepAt?.inputId);
            setAddStepAt(null);
          }}
        /> */}
        <RunDialog open={dialog === "run"} onClose={() => setDialog(null)} workflow={workflow} />
        {workflow._id && (
          <>
            <VersionsDialog open={dialog === "versions"} onClose={() => setDialog(null)} workflow={workflow} canEdit={canEdit} onRestored={reload} />
          </>
        )}
      </BuilderContext.Provider>
    </ConfigProvider>
  );
};

export default WorkflowBuilder;
